import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config";
import {
  ChefHat, ShoppingCart, IndianRupee, CreditCard, TrendingUp, X, Trash2
} from "lucide-react";
import "./Dashboard.css";
import Navbar from "../components/Navbar";

function Dashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(null); 
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [stats, setStats] = useState({
    allTables: [],
    activeTableIds: [],
    todaysSales: 0,
    gstCount: 0,
    nonGstCount: 0,
    gstBills: [],
    nonGstBills: [],
    recentOrders: []
  });

  const fetchDashboardData = async () => {
    try {
      const todayDate = new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API_BASE_URL}/reports.php`);
      const allSales = res.data || [];
      const savedTablesData = JSON.parse(localStorage.getItem("hotelTablesData") || "[]");
      const activeOrdersMap = JSON.parse(localStorage.getItem("activeTableOrders") || "{}");
      
      const allTables = savedTablesData.length > 0 ? savedTablesData : Array.from({ length: 15 }, (_, i) => ({ id: i + 1 }));
      const activeIds = Object.keys(activeOrdersMap).filter(id => activeOrdersMap[id]?.length > 0);
      const todaysBillsRaw = allSales.filter(bill => bill.sale_date === todayDate);

      const formattedBills = todaysBillsRaw.map((bill) => {
        const sameCategorySales = allSales.filter(s => s.table_id === bill.table_id);
        const categorySpecificIndex = sameCategorySales.length - sameCategorySales.indexOf(bill);
        const seq = categorySpecificIndex.toString().padStart(4, '0');
        const displayId = bill.table_id === "Walk-in" ? `S-WALK-${seq}` : `T${bill.table_id}-${seq}`;
        
        let parsedItems = [];
        try {
          parsedItems = typeof bill.items === 'string' ? JSON.parse(bill.items) : (bill.items || []);
        } catch (e) { console.error("Item parse error", e); }
        
        return { 
            ...bill, 
            displayId, 
            tableId: bill.table_id, 
            items: parsedItems,
            hasGST: parseFloat(bill.gst_amount) > 0,
            subtotal: parseFloat(bill.subtotal) || 0,
            gst_amount: parseFloat(bill.gst_amount) || 0,
            finalAmount: parseFloat(bill.grand_total) || 0
        };
      });

      setStats({
        allTables,
        activeTableIds: activeIds,
        todaysSales: formattedBills.reduce((acc, b) => acc + b.finalAmount, 0),
        gstCount: formattedBills.filter(b => b.hasGST).length,
        nonGstCount: formattedBills.filter(b => !b.nonGstCount).length,
        gstBills: formattedBills.filter(b => b.hasGST),
        nonGstBills: formattedBills.filter(b => !b.hasGST),
        recentOrders: [...formattedBills].reverse()
      });
    } catch (err) { console.error("Sync Error:", err); }
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleDeleteOrder = async (e, billNo) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this order?")) {
      try {
        const res = await axios.post(`${API_BASE_URL}/delete_bill.php`, { bill_no: billNo });
        if (res.data.status === "success") {
          fetchDashboardData();
        } else {
          alert("Error: " + res.data.message);
        }
      } catch (err) { alert("Connection Error"); }
    }
  };

  const toggleTab = (tab) => setActiveTab(activeTab === tab ? null : tab);
  const formatTableDisplay = (id) => (id === "Walk-in" ? "Walk-in" : `Table: ${id}`);

  return (
    <div className="page-layout">
      <Navbar />
      <div className="main-content-wrapper">
        <div className="dashboard-container">

        <header className="main-header mb-4">
          <h1 className="serif-title">Dashboard</h1>
          <p className="subtitle">Welcome back, Admin</p>
        </header>

        <div className="stats-grid">
          <div className={`stat-card clickable ${activeTab === 'total' ? 'active-card' : ''}`} onClick={() => toggleTab('total')}>
            <div className="stat-icon icon-red"><ChefHat size={22} /></div>
            <div className="stat-info">
              <span className="stat-label">TOTAL TABLES</span>
              <span className="stat-value">{stats.allTables.length}</span>
            </div>
          </div>
          <div className={`stat-card clickable ${activeTab === 'active' ? 'active-card' : ''}`} onClick={() => toggleTab('active')}>
            <div className="stat-icon icon-orange"><ShoppingCart size={22} /></div>
            <div className="stat-info">
              <span className="stat-label">ACTIVE TABLES</span>
              <span className="stat-value">{stats.activeTableIds.length}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon icon-green"><IndianRupee size={22} /></div>
            <div className="stat-info">
              <span className="stat-label">TODAY'S SALES</span>
              <span className="stat-value">₹{stats.todaysSales}</span>
            </div>
          </div>
          <div className={`stat-card clickable ${activeTab === 'gst' ? 'active-card' : ''}`} onClick={() => toggleTab('gst')}>
            <div className="stat-icon icon-brown"><CreditCard size={22} /></div>
            <div className="stat-info">
              <span className="stat-label">GST BILLS</span>
              <span className="stat-value">{stats.gstCount} / {stats.nonGstCount}</span>
            </div>
          </div>
        </div>

        <div className="detail-container">
          {activeTab === 'total' && (
            <div className="active-popup-card">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="popup-section-title">OCCUPIED TABLES</span>
                <X size={18} className="cursor-pointer" onClick={() => setActiveTab(null)} />
              </div>
              <div className="active-table-tags mb-4">
                {stats.allTables.filter(t => stats.activeTableIds.includes(t.id?.toString())).length > 0 ? (
                  stats.allTables.filter(t => stats.activeTableIds.includes(t.id?.toString())).map(t => (
                    <span key={t.id} className="tag-occupied" onClick={() => navigate(`/billing/${t.id}`)}>T{t.id}</span>
                  ))
                ) : <p className="text-muted small">No occupied tables</p>}
              </div>
              <span className="popup-section-title">AVAILABLE TABLES</span>
              <div className="active-table-tags mt-3">
                {stats.allTables.filter(t => !stats.activeTableIds.includes(t.id?.toString())).map(t => (
                  <span key={t.id} className="tag-available" onClick={() => navigate(`/billing/${t.id}`)}>T{t.id}</span>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'active' && (
            <div className="active-popup-card">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="popup-section-title">LIVE ORDERS</span>
                <X size={18} className="cursor-pointer" onClick={() => setActiveTab(null)} />
              </div>
              <div className="active-table-tags">
                {stats.activeTableIds.length > 0 ? (
                  stats.activeTableIds.map(id => (
                    <span key={id} className="tag-occupied" onClick={() => navigate(`/billing/${id}`)}>T{id}</span>
                  ))
                ) : <p className="text-muted small">No active orders</p>}
              </div>
            </div>
          )}

          {activeTab === 'gst' && (
            <div className="active-popup-card">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="popup-section-title">TODAY'S INVOICES</span>
                <X size={18} className="cursor-pointer" onClick={() => setActiveTab(null)} />
              </div>
              <div className="mb-3">
                <span className="gst-group-title">GST BILLS</span>
                <div className="active-table-tags mt-2">
                  {stats.gstBills.length > 0 ? stats.gstBills.map(b => (
                    <span key={b.id} className="tag-occupied" onClick={() => setSelectedOrder(b)}>{b.displayId}</span>
                  )) : <p className="text-muted small">None</p>}
                </div>
              </div>
              <div>
                <span className="gst-group-title">NON-GST BILLS</span>
                <div className="active-table-tags mt-2">
                  {stats.nonGstBills.length > 0 ? stats.nonGstBills.map(b => (
                    <span key={b.id} className="tag-available" onClick={() => setSelectedOrder(b)}>{b.displayId}</span>
                  )) : <p className="text-muted small">None</p>}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="orders-table-box mt-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="table-header-title m-0">Recent Completed Orders</h2>
            <TrendingUp size={20} color="#BA5028" />
          </div>
          <div className="table-responsive">
            <table className="ui-data-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Table</th>
                  <th>Items</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentOrders.length > 0 ? (
                  stats.recentOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="bold-id" onClick={() => setSelectedOrder(order)} style={{ cursor: 'pointer', color: '#BA5028', fontWeight: 'bold' }}>
                        #{order.displayId}
                      </td>
                      <td>{formatTableDisplay(order.tableId)}</td>
                      <td>{order.items.length} items</td>
                      <td className="bold-amount">₹{order.finalAmount}</td>
                      <td>
                        <span className={order.hasGST ? "gst-badge-rust" : "gst-badge-muted"}>
                          {order.hasGST ? "GST" : "Non-GST"}
                        </span>
                      </td>
                      <td className="text-center">
                        <Trash2 size={18} className="delete-icon" style={{ cursor: 'pointer', color: '#EF4444' }} onClick={(e) => handleDeleteOrder(e, order.bill_no)} />
                      </td>
                    </tr>
                  ))
                ) : (<tr><td colSpan="6" className="no-data-msg">No records found.</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>

      {/* DASHBOARD ORDER SUMMARY MODAL */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="bill-card dashboard-popup-bill" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="m-0 fw-bold">Order Summary</h3>
              <X size={20} className="cursor-pointer" onClick={() => setSelectedOrder(null)} />
            </div>
            <div className="items-list-scroll mb-3">
              {selectedOrder.items.map((item, idx) => (
                <div key={idx} className="bill-row d-flex justify-content-between py-1">
                  <span>{item.name} ({item.qty} pcs) × 1</span>
                  <span>₹{item.price * item.qty}</span>
                </div>
              ))}
            </div>
            
            <div className="border-top pt-2">
               <div className="d-flex justify-content-between text-muted small mb-1">
                  <span>Subtotal</span>
                  <span>₹{selectedOrder.subtotal}.00</span>
               </div>
               {selectedOrder.hasGST && (
                 <div className="d-flex justify-content-between text-muted small mb-1">
                    <span>GST (5%)</span>
                    <span>₹{selectedOrder.gst_amount}.00</span>
                 </div>
               )}
               <div className="border-top pt-2 fw-bold fs-5 d-flex justify-content-between">
                 <span>Grand Total</span>
                 <span>₹{selectedOrder.finalAmount}.00</span>
               </div>
            </div>

            <button className="summary-btn" onClick={() => setSelectedOrder(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;